import { SxProps, Theme } from "@mui/material";
import styled, { keyframes } from "styled-components";

import { PNG } from "@ryde/assets";
import { IStyledAttributes } from "@ryde/shared";
import {
  COMPACT_MOBILE_BREAKPOINT,
  deepPurpleMui,
  greyMui,
  MOBILE_BREAKPOINT,
  redMui,
  rydeColors,
} from "@ryde/theme";

import { LOGIN_PAGE_MOTION } from "./login.page.constants";

const loginTitleTextFadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const loginTitleLineGrow = keyframes`
  from {
    transform: scaleX(0);
  }
  to {
    transform: scaleX(1);
  }
`;

const loginFormReveal = keyframes`
  from {
    opacity: 0;
    transform: translateY(14px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const loginOrBadgeFade = keyframes`
  from {
    opacity: 0;
    transform: scale(0.94);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

const lineRevealAnimationRule = `${LOGIN_PAGE_MOTION.lineRevealDurationMs}ms ${LOGIN_PAGE_MOTION.lineRevealEasing} ${LOGIN_PAGE_MOTION.lineRevealDelayMs}ms both`;

/* eslint-disable @typescript-eslint/naming-convention -- MUI `sx` selector keys */
const loginOutlinedFieldRestSx: SxProps<Theme> = {
  transition: "box-shadow 0.2s ease, border-color 0.2s ease",
  boxShadow: "0 1px 3px rgba(49, 27, 146, 0.06)",
  "&:hover": {
    boxShadow: "0 2px 8px rgba(49, 27, 146, 0.1)",
  },
  "&.Mui-focused": {
    boxShadow: "0 0 0 3px rgba(103, 58, 183, 0.14)",
  },
};

export const textboxLoginSx: SxProps<Theme> = {
  backgroundFill: "white",
  "& .MuiOutlinedInput-root": loginOutlinedFieldRestSx,
  "&.MuiOutlinedInput-root": loginOutlinedFieldRestSx,
};

/* eslint-enable @typescript-eslint/naming-convention */

export const MainLoginContainerStyled = styled.div.attrs<IStyledAttributes>({
  "data-e2e": "main-login-container",
})`
  display: grid;
  justify-content: center;
  grid-template-columns: 640px 1fr 362px 1fr;
  grid-template-rows: 1fr 2fr 1fr 96px;
  grid-template-areas:
    "leftimage padgap centertop right"
    "leftimage padgap loginform right"
    "leftimage padgap centerbottom right"
    "leftimage bottombar bottombar bottombar";
  gap: 0 0;
  height: 100vh;
  width: 100vw;
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
    box-sizing: border-box;
    position: fixed;
    inset: 0;
    width: 100%;
    max-width: 100%;
    max-height: 100dvh;
    padding-block: max(16px, env(safe-area-inset-top, 0px))
      max(16px, env(safe-area-inset-bottom, 0px));
    padding-inline: max(16px, env(safe-area-inset-left, 0px))
      max(16px, env(safe-area-inset-right, 0px));
    overflow-x: hidden;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
  }
`;

export const LeftImageLogin = styled.div<{ $isAdminApp?: boolean }>`
  grid-area: leftimage;
  width: 640px;
  height: 100%;

  position: fixed;
  display: flex;
  align-items: center;
  justify-content: center;
  svg.logo {
    position: absolute;
    margin-top: 62px;
    inset-inline-start: 50px;
    align-self: flex-start;
  }
  background: no-repeat center
    ${({ $isAdminApp }) => {
      if ($isAdminApp) {
        return `url(${PNG.LoginAdminBgPNG}) ${rydeColors.bgColorLoginAdmin}`;
      }

      return `url(${PNG.LoginBgPNG}) ${rydeColors.bgColorLogin}`;
    }};

  background-size: cover;
`;

export const CopyrightLine = styled.div`
  grid-area: bottombar;
  display: flex;
  align-items: stretch;
  justify-content: center;
  color: rgba(0, 0, 0, 30%);
  font-size: 14px;
  div {
    border-top: 1px solid ${rydeColors.grey};
    width: 500px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    div {
      /* TODO: Temporarily size until the login page design will be provided */
      width: 95%;
    }
  }
`;

export const LoginFormStyled = styled.div.attrs<IStyledAttributes>({
  "data-e2e": "login-form",
})<{ $isNonProductionLoginColors?: boolean }>`
  grid-area: loginform;
  max-width: 347px;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  animation: ${loginFormReveal} ${LOGIN_PAGE_MOTION.formRevealDurationMs}ms
    ${LOGIN_PAGE_MOTION.formRevealEasing}
    ${LOGIN_PAGE_MOTION.formRevealDelayMs}ms both;

  .MuiFormControl-root {
    width: 100%;
  }

  .authButtons {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
    margin-bottom: 8px;
  }

  .LoginGenericForm {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 16px;
    margin-top: 8px;

    .policyText {
      line-height: 1.6;
      a {
        text-decoration: underline;
        color: ${deepPurpleMui.A400};
        margin-inline-start: 5px;
        margin-inline-end: 5px;
      }
    }

    .LoginGenericForm-ResendLink {
      cursor: pointer;

      &.disabled {
        cursor: default;
        pointer-events: none;
        opacity: 0.5;
      }
    }
  }

  .LoginGenericForm-LabelSignIn {
    line-height: 16px;
    font-size: 12px;
    font-weight: 700;
    color: ${greyMui._700};
  }

  .LoginGenericForm-FieldContainerLabels {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;

    @media (max-width: ${COMPACT_MOBILE_BREAKPOINT}) {
      flex-wrap: wrap;
    }
  }
  .LoginGenericForm-ForgotPasswordLabel {
    color: ${deepPurpleMui.A400};
    cursor: pointer;
  }
  .LoginGenericForm-FieldContainerLabelsAstrix {
    color: ${redMui._600};
  }

  ${props =>
    props.$isNonProductionLoginColors
      ? `
    .LoginGenericForm .policyText a,
    .LoginGenericForm-ForgotPasswordLabel,
    .LoginGenericForm-ResendLink,
    .LoginGenericForm .MuiLink-root {
      color: ${rydeColors.nonProductionNavbarOrangeHover};
    }
  `
      : ""}

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    width: 100%;
    max-width: 347px;
    flex-shrink: 0;
    padding-inline: 0;
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    opacity: 1;
    transform: none;
  }
`;

export const TitleLineStyled = styled.h1.attrs<IStyledAttributes>({
  "data-e2e": "login-title-line",
})<{ $isNonProductionLoginColors?: boolean }>`
  margin: 0 0 28px;
  padding: 0;
  width: 100%;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 12px;
  font-size: 42px;
  font-weight: 500;
  line-height: 1.12;
  letter-spacing: -0.03em;
  color: ${props =>
    props.$isNonProductionLoginColors
      ? rydeColors.nonProductionNavbarOrangeHover
      : deepPurpleMui._900};
  text-shadow: ${props =>
    props.$isNonProductionLoginColors
      ? "0 1px 2px rgba(153, 56, 0, 0.08)"
      : "0 1px 2px rgba(49, 27, 146, 0.08)"};

  & .login-title-row {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: flex-start;
    column-gap: 10px;
    row-gap: 4px;
  }

  & .login-title-primary {
    display: inline-block;
    color: ${props =>
      props.$isNonProductionLoginColors
        ? rydeColors.nonProductionNavbarOrangeHover
        : deepPurpleMui._900};
    font-weight: 500;
    animation: ${loginTitleTextFadeIn} 1.55s ease-out both;
  }

  & .login-title-admin {
    display: inline-flex;
    align-items: center;
    flex-shrink: 0;
    margin-inline-start: 3px;
    font-size: 0.46em;
    font-weight: 700;
    line-height: 1;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: ${props =>
      props.$isNonProductionLoginColors
        ? rydeColors.nonProductionNavbarOrangeHover
        : deepPurpleMui.A400};
    padding: 0.32em 0.52em;
    border-radius: 8px;
    border: 1px solid
      ${props =>
        props.$isNonProductionLoginColors
          ? "rgba(204, 110, 0, 0.35)"
          : deepPurpleMui._200};
    background: ${props =>
      props.$isNonProductionLoginColors
        ? `linear-gradient(
      180deg,
      rgba(255, 214, 170, 0.65) 0%,
      rgba(255, 255, 255, 0.9) 100%
    )`
        : `linear-gradient(
      180deg,
      rgba(237, 231, 246, 0.65) 0%,
      rgba(255, 255, 255, 0.9) 100%
    )`};
    box-shadow: ${props =>
      props.$isNonProductionLoginColors
        ? "0 1px 3px rgba(153, 56, 0, 0.08)"
        : "0 1px 3px rgba(49, 27, 146, 0.08)"};
    text-shadow: none;
    animation: ${loginTitleTextFadeIn} 1.85s ease-out 0.22s both;
  }

  &::after {
    content: "";
    display: block;
    width: 100%;
    height: 1px;
    flex-shrink: 0;
    border-radius: 0;
    background: ${props =>
      props.$isNonProductionLoginColors
        ? rydeColors.nonProductionNavbarOrangeHover
        : rydeColors.purpleTitle};
    box-shadow: none;
    transform-origin: center center;
    animation: ${loginTitleLineGrow} ${lineRevealAnimationRule};
  }

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    font-size: 32px;
    gap: 10px;
    margin-bottom: 20px;
    color: ${props =>
      props.$isNonProductionLoginColors
        ? rydeColors.nonProductionNavbarOrangeHover
        : deepPurpleMui._600};

    & .login-title-primary {
      color: ${props =>
        props.$isNonProductionLoginColors
          ? rydeColors.nonProductionNavbarOrangeHover
          : deepPurpleMui._600};
    }

    & .login-title-admin {
      font-size: 0.52em;
      letter-spacing: 0.06em;
    }

    &::after {
      width: 100%;
      height: 1px;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    & .login-title-primary,
    & .login-title-admin {
      animation: none;
      opacity: 1;
    }

    &::after {
      animation: none;
      transform: scaleX(1);
    }
  }
`;

export const LoginOrSeparatorStyled = styled.div.attrs<IStyledAttributes>({
  "data-e2e": "login-or-separator",
})`
  display: flex;
  flex-direction: row;
  align-items: center;
  width: 100%;
  margin: 2px 0 10px;
  min-height: 44px;

  & .login-or-separator__track {
    flex: 1 1 0;
    min-width: 0;
    display: flex;
    align-items: center;
  }

  & .login-or-separator__track--before {
    justify-content: flex-end;
  }

  & .login-or-separator__track--after {
    justify-content: flex-start;
  }

  & .login-or-separator__line {
    display: block;
    width: 100%;
    height: 1px;
    border-radius: 0;
    background: ${rydeColors.purpleTitle};
    transform: scaleX(0);
    animation: ${loginTitleLineGrow} ${lineRevealAnimationRule};
  }

  & .login-or-separator__track--before .login-or-separator__line {
    transform-origin: inline-end center;
  }

  & .login-or-separator__track--after .login-or-separator__line {
    transform-origin: inline-start center;
  }

  & .login-or-separator__badge {
    flex-shrink: 0;
    box-sizing: border-box;
    min-width: 44px;
    height: 44px;
    margin-inline: 10px;
    padding: 0 10px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    background: ${rydeColors.purpleTitle};
    border: 1px solid ${rydeColors.greyBorderButton};
    color: ${rydeColors.white};
    font-size: 12px;
    font-weight: 700;
    line-height: 1;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    box-shadow: 0 1px 3px rgba(49, 27, 146, 0.14);
    cursor: pointer;
    user-select: none;
    transition:
      transform 0.18s ease,
      box-shadow 0.18s ease;

    &:hover {
      transform: scale(1.03);
      box-shadow: 0 2px 8px rgba(49, 27, 146, 0.22);
    }

    &:active {
      transform: scale(0.98);
    }

    &:focus-visible {
      outline: 2px solid ${deepPurpleMui.A200};
      outline-offset: 3px;
    }

    animation: ${loginOrBadgeFade} 0.55s ease-out
      ${LOGIN_PAGE_MOTION.lineRevealDelayMs}ms both;
  }

  & .login-or-separator__badge:dir(rtl) {
    text-transform: none;
    letter-spacing: 0.02em;
    font-size: 15px;
    font-weight: 700;
  }

  @media (prefers-reduced-motion: reduce) {
    & .login-or-separator__line {
      animation: none;
      transform: scaleX(1);
    }

    & .login-or-separator__badge {
      animation: none;
      opacity: 1;
      transform: none;

      &:hover,
      &:active {
        transform: none;
        box-shadow: 0 1px 3px rgba(49, 27, 146, 0.14);
      }
    }
  }
`;

export const LoginControlContainerStyled = styled.div.attrs<IStyledAttributes>({
  "data-e2e": "login-controls-container",
})`
  margin-bottom: 20px;
`;
